/**
 * App shell with routing and layout.
 */
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { initSentry } from "@/global/lib/sentry";
import { AuthProvider } from "@/global/auth/AuthContext";
import { Layout } from "@/shared/components/Layout";
import { ConfigurationProvider } from "@/global/ConfigurationContext";
import { ThemeProvider } from "@/global/components/ui/theme-provider";
import { Toaster } from "@/global/components/ui/toaster";
import { AccessGuard } from "@/global/components/auth/AccessGuard";
import { AuthConsume } from "@/global/components/auth/AuthConsume";
import { PostHogProvider, PageViewTracker } from "@/global/lib/analytics";
import { LoginPage } from "@/global/components/auth/LoginPage";
import { TourProvider } from "@/global/contexts/TourContext";
import { JoyrideProvider } from "@/global/components/tour/TourProvider";
import { OrganizationSelectPage } from "@/global/components/auth/OrganizationSelectPage";
import { SoftphoneWrapper } from "@/components/twilio/SoftphoneWrapper";
import { CookieConsentBanner } from "@/global/components/CookieConsentBanner";
import PricingPage from "@/pages/PricingPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import FMAWelcomePage from "@/pages/FMAWelcomePage";
import AppChoicePage from "@/pages/AppChoicePage";
import SignUpPage from "@/pages/SignUpPage";
import AgentSignUpPage from "@/global/pages/AgentSignUpPage";
import OrcaAccountPage from "@/global/pages/OrcaAccountPage";
import TermsAndConditionsPage from "@/global/pages/TermsAndConditionsPage";

// Code-split heavy routes to reduce initial bundle
const AdminApp = lazy(() => import("@/shared/components/admin/AdminApp"));
const Dashboard = lazy(() => import("../pages/Dashboard/index"));
const AgentsList = lazy(() => import("../pages/Agents/AgentsList"));
const AgentCreate = lazy(() => import("../pages/Agents/AgentCreate"));
const AgentDetails = lazy(() => import("../pages/Agents/AgentDetails"));
const AgencyList = lazy(() => import("../pages/Agencies/AgencyList"));
const AgencyCreate = lazy(() => import("../pages/Agencies/AgencyCreate"));
const AgencyDetails = lazy(() => import("../pages/Agencies/AgencyDetails"));
const ClientList = lazy(() => import("../pages/Clients/ClientList"));
const ClientCreate = lazy(() => import("../pages/Clients/ClientCreate"));
const ClientDetails = lazy(() => import("../pages/Clients/ClientDetails"));
const Analytics = lazy(() => import("../pages/Analytics"));
const Emails = lazy(() => import("../pages/Emails"));
const Settings = lazy(() => import("../pages/Settings"));
const CookieSettings = lazy(() => import("../pages/Settings/CookieSettings"));
const PrivacyPolicy = lazy(() => import("../pages/Legal/PrivacyPolicy"));
const BillingTest = lazy(() => import("../pages/BillingTest"));
const AdminDashboard = lazy(() => import("../pages/Admin/AdminDashboard"));
const UserManagement = lazy(() => import("../pages/Admin/UserManagement"));
const UserManagementNew = lazy(() => import("../pages/Admin/UserManagementNew"));
const ActivityLogs = lazy(() => import("../pages/Admin/ActivityLogs"));
const SystemHealth = lazy(() => import("../pages/Admin/SystemHealth"));
const SubscriptionAnalytics = lazy(
  () => import("../pages/Admin/SubscriptionAnalytics")
);
const TaskManager = lazy(() => import("../pages/Admin/TaskManager"));
const FMAApp = lazy(() =>
  import("../../srcFMA/root/FMAApp").then((m) => ({ default: m.FMAApp }))
);
const InvitationSignUp = lazy(() => import("../pages/SignUp/InvitationSignUp"));
const PendingInvitationsPage = lazy(() => import("../pages/Invitations/PendingInvitationsPage"));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

export function App() {
  // Initialize Sentry error tracking (production only)
  useEffect(() => {
    initSentry();
  }, []);

  return (
    <ConfigurationProvider>
      <ThemeProvider defaultTheme="dark" storageKey="orcaams-theme">
        <AuthProvider>
          <TourProvider>
            <JoyrideProvider>
              <PostHogProvider>
                <CookieConsentBanner />
                <Toaster />
                <SoftphoneWrapper />
            <BrowserRouter>
              <PageViewTracker appType="AMS" />
              <Routes>
              {/* Public routes - no authentication required */}
              <Route path="/" element={<PricingPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/signup" element={<SignUpPage />} />
              <Route 
                path="/sign-up/:token" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <InvitationSignUp />
                  </Suspense>
                } 
              />
              <Route path="/agent-signup" element={<AgentSignUpPage />} />

              {/* Authentication routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route
                path="/billing-test"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <BillingTest />
                  </Suspense>
                }
              />
              <Route path="/app-choice" element={<AppChoicePage />} />
              <Route path="/fma-welcome" element={<FMAWelcomePage />} />
              <Route path="/orca-account" element={<OrcaAccountPage />} />
              <Route path="/terms" element={<TermsAndConditionsPage />} />
              <Route 
                path="/privacy" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <PrivacyPolicy />
                  </Suspense>
                } 
              />
              <Route
                path="/invitations"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <PendingInvitationsPage />
                  </Suspense>
                }
              />
              <Route
                path="/auth/payment-success"
                element={<PaymentSuccessPage />}
              />
              <Route
                path="/select-organization"
                element={<OrganizationSelectPage />}
              />
              <Route path="/auth/consume" element={<AuthConsume />} />

              {/* FMA App Routes (Find My Agency marketplace) */}
              <Route
                path="/fma/*"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <FMAApp />
                  </Suspense>
                }
              />

              {/* Protected react-admin routes - using /ra path to avoid conflict */}
              <Route
                path="/ra/*"
                element={
                  <AccessGuard app="AMS">
                    <Suspense fallback={<PageLoader />}>
                      <AdminApp />
                    </Suspense>
                  </AccessGuard>
                }
              />

              {/* Protected main app routes */}
              <Route
                path="/"
                element={
                  <AccessGuard app="AMS">
                    <Layout />
                  </AccessGuard>
                }
              >
                <Route
                  path="dashboard"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <Dashboard />
                    </Suspense>
                  }
                />
                <Route
                  path="agents"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <AgentsList />
                    </Suspense>
                  }
                />
                <Route
                  path="agents/create"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <AgentCreate />
                    </Suspense>
                  }
                />
                <Route
                  path="agents/:id"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <AgentDetails />
                    </Suspense>
                  }
                />
                <Route
                  path="agencies"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <AgencyList />
                    </Suspense>
                  }
                />
                <Route
                  path="agencies/create"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <AgencyCreate />
                    </Suspense>
                  }
                />
                <Route
                  path="agencies/:id"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <AgencyDetails />
                    </Suspense>
                  }
                />
                <Route
                  path="clients"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ClientList />
                    </Suspense>
                  }
                />
                <Route
                  path="clients/create"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ClientCreate />
                    </Suspense>
                  }
                />
                <Route
                  path="clients/:id"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ClientDetails />
                    </Suspense>
                  }
                />
                <Route
                  path="analytics"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <Analytics />
                    </Suspense>
                  }
                />
                <Route
                  path="emails"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <Emails />
                    </Suspense>
                  }
                />
                <Route
                  path="settings"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <Settings />
                    </Suspense>
                  }
                />
                <Route
                  path="settings/cookies"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <CookieSettings />
                    </Suspense>
                  }
                />

                {/* Admin Routes - Only visible to super admins */}
                <Route
                  path="admin/dashboard"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <AdminDashboard />
                    </Suspense>
                  }
                />
                <Route
                  path="admin/users"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <UserManagement />
                    </Suspense>
                  }
                />
                <Route
                  path="admin/users-new"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <UserManagementNew />
                    </Suspense>
                  }
                />
                <Route
                  path="admin/activity"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ActivityLogs />
                    </Suspense>
                  }
                />
                <Route
                  path="admin/system"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <SystemHealth />
                    </Suspense>
                  }
                />
                <Route
                  path="admin/subscriptions"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <SubscriptionAnalytics />
                    </Suspense>
                  }
                />
                <Route
                  path="admin/tasks"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <TaskManager />
                    </Suspense>
                  }
                />
              </Route>
            </Routes>
          </BrowserRouter>
              </PostHogProvider>
            </JoyrideProvider>
          </TourProvider>
        </AuthProvider>
      </ThemeProvider>
    </ConfigurationProvider>
  );
}
